import type { IEventService } from '@realitycollective/service-framework';
import { createServiceToken } from '@realitycollective/service-framework';
import type { AssistantEntry } from '../../core/assistantRoster';

export type AssistantRosterEventMap = {
  /** An assistant joined the session (toast + chime material - RA-C5). */
  assistantJoined: AssistantEntry;
  /** An assistant left the session. */
  assistantLeft: AssistantEntry;
  /** An assistant's AV/observer/hide/mute state changed. */
  assistantChanged: AssistantEntry;
  /** `assistantsMuted` changed. */
  allMutedChanged: boolean;
};

/**
 * Owns the assistant model (Plan-01 step 4, RA-C5): id, name, observer flag,
 * published tracks, and the wearer's hide/mute state. Consumes room
 * participant events plus the `presence` topic, applies the wearer's D5
 * subscription toggles, and echoes the hints back on `presence` as
 * `wearerState`. Persists nothing. Port of the Unity `IAssistantRosterService`.
 */
export interface IAssistantRosterService extends IEventService<AssistantRosterEventMap> {
  /** Join-ordered snapshot of the current assistants. */
  readonly assistants: readonly AssistantEntry[];
  readonly count: number;

  /**
   * Hide/show this assistant's video for the wearer only (D5: a local
   * subscription toggle - dynacast stops the upstream publish when nobody
   * subscribes; the console sees "hidden by wearer" via presence).
   */
  setHidden(identity: string, hidden: boolean): void;

  /** Mute/unmute this assistant's audio for the wearer only (D5). */
  setMuted(identity: string, muted: boolean): void;

  /**
   * The wearer's standing "I do not want to hear the assistant" choice - what
   * the hand-menu tile shows and what the NEXT assistant to join inherits. The
   * wearer mutes the role, not the person.
   */
  readonly assistantsMuted: boolean;

  /** Mute/unmute every assistant at once and remember the choice for whoever joins next. Video is untouched. */
  setAllMuted(muted: boolean): void;

  /** Flip `assistantsMuted` (the hand-menu tile). */
  toggleAllMuted(): void;

  /**
   * The consent machine's assist state (`live` / `paused`, or empty for off).
   * Paused unsubscribes every assistant track; live restores each assistant's
   * own hide/mute choices; off broadcasts nothing.
   */
  setAssistState(assistToken: string): void;
}

export const IAssistantRosterService = createServiceToken<IAssistantRosterService>('IAssistantRosterService');
